import { React, useState, useEffect, useRef } from 'react'
import { Container, Row, Col, Alert, Spinner, Button, Card, Offcanvas, Form, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import Login from '../Components/Admin/Login';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import Navigation from '../Components/Admin/Navigation';
import UkiesFullsizeSlider from '../Components/Designer/UkiesFullsizeSlider';
import UkiesFullsizeBanner from '../Components/Designer/UkiesFullsizeBanner';
import UkiesPhotoText from '../Components/Designer/UkiesPhotoText';
import Ukies22Block from '../Components/Designer/Ukies-2-2-Block';
import UkiesFAQ from '../Components/Designer/UkiesFAQ';
import UkiesWidget from '../Components/Designer/UkiesWidget';
import UkiesForm from '../Components/Designer/UkiesForm';
import UkiesNoEdits from '../Components/Designer/UkiesNoEdits';
import UkiesHighlightBar from '../Components/Designer/UkiesHighlightBar';
import UkiesTextWithBlocks from '../Components/Designer/UkiesTextWithBlocks';
import UkiesStraplineTitle from '../Components/Designer/UkiesStraplineTitle';
import UkiesPhotoGallery from '../Components/Designer/UkiesPhotoGallery';
import UkiesSpacer from '../Components/Designer/UkiesSpacer';

// Component to handle HTML content with script execution
function HTMLWithScripts({ html, style, className, componentId, forms = [], widgets = [] }) {
    const containerRef = useRef(null);
    const executedScripts = useRef(new Set());

    // Process shortcodes in HTML content
    const processShortcodes = (htmlContent) => {
        if (!htmlContent) return htmlContent;
        
        let processedContent = htmlContent;
        
        // console.log('Processing shortcodes. Forms:', forms.length, 'Widgets:', widgets.length);
        // console.log('HTML content:', htmlContent);
        
        // Process widget shortcodes [widget=ID]
        processedContent = processedContent.replace(/\[widget=([^\]]+)\]/g, (match) => {
            // console.log('Found widget shortcode:', match);
            const widget = widgets.find(w => w.shortcode === match);
            // console.log('Matching widget:', widget);
            if (widget) {
                // Use output if available and not empty, otherwise fall back to content
                return widget.output || widget.content || '';
            }
            return match;
        });
        
        // Process form shortcodes [form=ID]
        processedContent = processedContent.replace(/\[form=([^\]]+)\]/g, (match) => {
            // console.log('Found form shortcode:', match);
            const form = forms.find(f => f.shortcode === match);
            // console.log('Matching form:', form);
            if (form) {
                // Use output if available and not empty, otherwise fall back to content
                return form.output || form.content || '';
            }
            return match;
        });
        
        // console.log('Processed content:', processedContent);
        return processedContent;
    };

    useEffect(() => {
        if (!containerRef.current || !html) return;

        // Clear executed scripts cache when content changes to allow re-execution
        executedScripts.current.clear();

        // Process shortcodes in the HTML before setting it
        const processedHtml = processShortcodes(html);

        // Set the HTML content
        containerRef.current.innerHTML = processedHtml;

        // Find and execute all script tags
        const scripts = containerRef.current.querySelectorAll('script');
        scripts.forEach((script, index) => {
            try {
                // Create a unique identifier for the script including component ID
                const scriptContent = script.src || script.innerHTML;
                const scriptId = `${componentId || 'unknown'}-${index}-${btoa(scriptContent).slice(0, 10)}`;
                
                // Skip if we've already executed this script in this render cycle
                if (executedScripts.current.has(scriptId)) return;
                
                // Create a new script element
                const newScript = document.createElement('script');
                
                // Copy all attributes
                Array.from(script.attributes).forEach(attr => {
                    newScript.setAttribute(attr.name, attr.value);
                });
                
                // Wrap script content in an IIFE to avoid global variable conflicts
                if (script.src) {
                    newScript.src = script.src;
                } else {
                    // Wrap inline scripts in an IIFE to prevent variable conflicts
                    const wrappedContent = `(function() { try { ${script.innerHTML} } catch(e) { console.warn('Script execution error:', e); } })();`;
                    newScript.innerHTML = wrappedContent;
                }
                
                // Replace the old script with the new one to execute it
                script.parentNode.replaceChild(newScript, script);
                
                // Mark as executed for this render cycle
                executedScripts.current.add(scriptId);
                
            } catch (error) {
                console.warn('Error executing script:', error);
            }
        });

        // Cleanup function to remove scripts when component unmounts or content changes
        return () => {
            if (containerRef.current) {
                const scripts = containerRef.current.querySelectorAll('script');
                scripts.forEach(script => {
                    try {
                        if (script.parentNode) {
                            script.parentNode.removeChild(script);
                        }
                    } catch (error) {
                        console.warn('Error removing script:', error);
                    }
                });
            }
        };
    }, [html, componentId, forms, widgets]);

    return (
        <div 
            ref={containerRef} 
            style={style}
            className={className}
        />
    );
}

// Function to extract ukies-* class from HTML content
function extractComponentType(htmlContent) {
    if (!htmlContent) return null;
    
    try {
        // Create a temporary DOM element to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent.trim();
        
        // Get the first child element (outermost div)
        const outerElement = tempDiv.firstElementChild;
        if (!outerElement) return null;
        
        // Get all classes from the outer element
        const classes = Array.from(outerElement.classList || []);
        
        // Find the class that starts with "ukies-" but ignore the archived flag class
        const ukiesClass = classes.find(className => className.startsWith('ukies-') && className !== 'ukies-archived');
        
        return ukiesClass || null;
    } catch (error) {
        console.error('Error parsing HTML content:', error);
        return null;
    }
}

// Function to update HTML content with new ukies-* class
function updateComponentTypeInHTML(htmlContent, newComponentType) {
    if (!htmlContent) return htmlContent;
    
    try {
        // Create a temporary DOM element to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent.trim();
        
        // Get the first child element (outermost div)
        const outerElement = tempDiv.firstElementChild;
        if (!outerElement) return htmlContent;
        
        // Remove any existing ukies-* classes
        const existingClasses = Array.from(outerElement.classList || []);
        existingClasses.forEach(className => {
            if (className.startsWith('ukies-') && className !== 'ukies-archived') {
                outerElement.classList.remove(className);
            }
        });
        
        // Add the new ukies-* class if provided
        if (newComponentType && newComponentType.startsWith('ukies-')) {
            outerElement.classList.add(newComponentType);
        }
        
        // Return the updated HTML
        return tempDiv.innerHTML;
    } catch (error) {
        console.error('Error updating HTML content:', error);
        return htmlContent;
    }
}

// Determine if the component is archived by checking for the 'ukies-archived' class on the outermost element
function isComponentArchived(htmlContent) {
    if (!htmlContent) return false;
    try {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = (htmlContent || '').trim();
        const outerElement = tempDiv.firstElementChild;
        if (!outerElement) return false;
        const classes = Array.from(outerElement.classList || []);
        return classes.includes('ukies-archived');
    } catch (error) {
        console.error('Error checking archived state from HTML content:', error);
        return false;
    }
}

// Toggle the 'ukies-archived' class on the outermost element in the HTML content
function updateArchivedStatusInHTML(htmlContent, shouldBeArchived) {
    if (!htmlContent) return htmlContent;
    try {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = (htmlContent || '').trim();
        const outerElement = tempDiv.firstElementChild;
        if (!outerElement) return htmlContent;

        if (shouldBeArchived) {
            outerElement.classList.add('ukies-archived');
        } else {
            outerElement.classList.remove('ukies-archived');
        }
        return tempDiv.innerHTML;
    } catch (error) {
        console.error('Error updating archived state in HTML content:', error);
        return htmlContent;
    }
}

// Component to render appropriate designer component based on component type
function ComponentSettingsForm({ componentType, componentSettings, setComponentSettings, onChange, selectedComponent, setSelectedComponent, forms, widgets }) {
    switch (componentType) {
        case 'ukies-fullsize-slider-banner':
            return (
                <UkiesFullsizeSlider 
                    componentSettings={componentSettings}
                    setComponentSettings={setComponentSettings}
                    onChange={onChange}
                    selectedComponent={selectedComponent}
                    setSelectedComponent={setSelectedComponent}
                />
            );
            
        case 'ukies-fullsize-banner':
            return (
                <UkiesFullsizeBanner 
                    componentSettings={componentSettings}
                    setComponentSettings={setComponentSettings}
                    onChange={onChange}
                    selectedComponent={selectedComponent}
                    setSelectedComponent={setSelectedComponent}
                />
            );
            
        case 'ukies-photo-text':
            return (
                <UkiesPhotoText 
                    componentSettings={componentSettings}
                    setComponentSettings={setComponentSettings}
                    onChange={onChange}
                    selectedComponent={selectedComponent}
                    setSelectedComponent={setSelectedComponent}
                />
            );
            
        case 'ukies-2-2-block':
            return (
                <Ukies22Block 
                    componentSettings={componentSettings}
                    setComponentSettings={setComponentSettings}
                    onChange={onChange}
                    selectedComponent={selectedComponent}
                    setSelectedComponent={setSelectedComponent}
                    forms={forms}
                />
            );
            
        case 'ukies-faq':
            return (
                <UkiesFAQ 
                    componentSettings={componentSettings}
                    setComponentSettings={setComponentSettings}
                    onChange={onChange}
                    selectedComponent={selectedComponent}
                    setSelectedComponent={setSelectedComponent}
                />
            );
            
        case 'ukies-widget':
            return (
                <UkiesWidget 
                    componentSettings={componentSettings}
                    setComponentSettings={setComponentSettings}
                    onChange={onChange}
                    selectedComponent={selectedComponent}
                    setSelectedComponent={setSelectedComponent}
                    widgets={widgets}
                />
            );
            
        case 'ukies-form':
            return (
                <UkiesForm 
                    componentSettings={componentSettings}
                    setComponentSettings={setComponentSettings}
                    onChange={onChange}
                    selectedComponent={selectedComponent}
                    setSelectedComponent={setSelectedComponent}
                    forms={forms}
                />
            );
            
        case 'ukies-highlight-bar':
            return (
                <UkiesHighlightBar 
                    componentSettings={componentSettings}
                    setComponentSettings={setComponentSettings}
                    onChange={onChange}
                    selectedComponent={selectedComponent}
                    setSelectedComponent={setSelectedComponent}
                />
            );
            
        case 'ukies-text-with-blocks':
            return (
                <UkiesTextWithBlocks 
                    componentSettings={componentSettings}
                    setComponentSettings={setComponentSettings}
                    onChange={onChange}
                    selectedComponent={selectedComponent}
                    setSelectedComponent={setSelectedComponent}
                    forms={forms}
                />
            );
            
        case 'ukies-strapline-title':
            return (
                <UkiesStraplineTitle 
                    componentSettings={componentSettings}
                    setComponentSettings={setComponentSettings}
                    onChange={onChange}
                    selectedComponent={selectedComponent}
                    setSelectedComponent={setSelectedComponent}
                    forms={forms}
                />
            );
            
        case 'ukies-no-edits':
            return (
                <UkiesNoEdits
                    componentSettings={componentSettings}
                    setComponentSettings={setComponentSettings}
                    onChange={onChange}
                    selectedComponent={selectedComponent}
                    setSelectedComponent={setSelectedComponent}
                />
            );

        case 'ukies-photo-gallery':
            return (
                <UkiesPhotoGallery
                    componentSettings={componentSettings}
                    setComponentSettings={setComponentSettings}
                    onChange={onChange}
                    selectedComponent={selectedComponent}
                    setSelectedComponent={setSelectedComponent}
                />
            );

        case 'ukies-spacer':
            return (
                <UkiesSpacer
                    componentSettings={componentSettings}
                    setComponentSettings={setComponentSettings}
                    onChange={onChange}
                    selectedComponent={selectedComponent}
                    setSelectedComponent={setSelectedComponent}
                />
            );

        default:
            return (
                <div className="text-center py-4">
                    <p className="text-muted mb-2">
                        Geen specifieke instellingen beschikbaar voor deze component.
                    </p>
                    <small className="text-muted">
                        Component type: <code>{componentType || 'unknown'}</code>
                    </small>
                </div>
            );
    }
}

export default function UkiesAdminPageDesigner(props) {
    const auth = getAuth();
    const navigate = useNavigate();
    const { pageId } = useParams();
    const [ token, setToken ] = useState(localStorage.getItem('ukies-webinars-accessToken'))
    const [ user, setUser ] = useState(JSON.parse(localStorage.getItem('ukies-webinars-admin')))
    
    // Page and components state
    const [ page, setPage ] = useState(null)
    const [ components, setComponents ] = useState([])
    const [ loading, setLoading ] = useState(false)
    const [ error, setError ] = useState(null)
    const [ showAlert, setShowAlert ] = useState(false)
    const [ alertMessage, setAlertMessage ] = useState('')
    const [ alertType, setAlertType ] = useState('success')
    
    // Forms and widgets state for shortcode processing
    const [ forms, setForms ] = useState([])
    const [ widgets, setWidgets ] = useState([])
    
    // Selection and menu state
    const [ selectedComponent, setSelectedComponent ] = useState(null)
    const [ showSettings, setShowSettings ] = useState(false)
    const [ componentType, setComponentType ] = useState(null)
    const [ componentSettings, setComponentSettings ] = useState({})
    const [ deleteEnabled, setDeleteEnabled ] = useState(false)

    // Add component dialog state
    const [ showAddModal, setShowAddModal ] = useState(false)
    const [ newComponentLabel, setNewComponentLabel ] = useState('')
    const [ newComponentType, setNewComponentType ] = useState('')
    const [ newComponentVisible, setNewComponentVisible ] = useState(true)
    
    // Drag and drop state
    const [ draggedComponent, setDraggedComponent ] = useState(null)
    const [ dragOverIndex, setDragOverIndex ] = useState(null)
    
    // Auto-scroll state
    const [ autoScrollInterval, setAutoScrollInterval ] = useState(null)
    const [ autoScrollTimeout, setAutoScrollTimeout ] = useState(null)
    
    // Filter state
    const [ showVisibleComponents, setShowVisibleComponents ] = useState(true)
    const [ showInvisibleComponents, setShowInvisibleComponents ] = useState(false)
    const [ showArchivedComponents, setShowArchivedComponents ] = useState(false)
    
    // Unsaved changes tracking
    const [ unsavedChanges, setUnsavedChanges ] = useState(new Set())
    
    // Token for API calls
    const apiToken = process.env.REACT_APP_UKIES_API_TOKEN;
    
    // Computed filtered components based on visibility and archived toggles
    const filteredComponents = components.filter(component => {
        const isActive = component.component_status === null || component.component_status === undefined ? true : component.component_status === 1;
        const archived = isComponentArchived(component.content);
        if (isActive) {
            return showVisibleComponents;
        }
        // Not active
        if (archived) {
            return showArchivedComponents;
        }
        return showInvisibleComponents;
    });
    
    // Helper functions for unsaved changes tracking
    const markComponentAsChanged = (componentId) => {
        setUnsavedChanges(prev => new Set([...prev, componentId]));
    };
    
    const markComponentAsSaved = (componentId) => {
        setUnsavedChanges(prev => {
            const newSet = new Set(prev);
            newSet.delete(componentId);
            return newSet;
        });
    };
    
    const hasUnsavedChanges = (componentId) => {
        return unsavedChanges.has(componentId);
    };
    
    // Auto-scroll functions
    const startAutoScroll = (direction, speed) => {
        if (autoScrollInterval) return; // Already scrolling
        
        const scrollAmount = Math.max(5, Math.min(10, speed)); // Clamp speed
        
        const interval = setInterval(() => {
            if (direction === 'up') {
                window.scrollBy(0, -scrollAmount);
            } else if (direction === 'down') {
                window.scrollBy(0, scrollAmount);
            }
        }, 16); // ~60fps
        
        setAutoScrollInterval(interval);
        
        // Safety timeout - stop auto-scroll after 200ms if not refreshed
        if (autoScrollTimeout) {
            clearTimeout(autoScrollTimeout);
        }
        
        const timeout = setTimeout(() => {
            stopAutoScroll();
        }, 200);
        setAutoScrollTimeout(timeout);
    };

    const stopAutoScroll = () => {
        if (autoScrollInterval) {
            clearInterval(autoScrollInterval);
            setAutoScrollInterval(null);
        }
        if (autoScrollTimeout) {
            clearTimeout(autoScrollTimeout);
            setAutoScrollTimeout(null);
        }
    };

    // Handle drag over for auto-scroll (works better than mousemove during drag)
    const handleContainerDragOver = (e) => {
        e.preventDefault(); // Prevent default to allow drop
        if (!draggedComponent) {
            stopAutoScroll(); // Stop if no component is being dragged
            return;
        }
        
        const threshold = 100; // Distance from edge to start scrolling
        const viewportHeight = window.innerHeight;
        const mouseY = e.clientY;
        
        // Calculate distances from edges
        const distanceFromTop = mouseY;
        const distanceFromBottom = viewportHeight - mouseY;
        
        if (distanceFromTop < threshold) {
            // Near top edge - scroll up
            const proximity = (threshold - distanceFromTop) / threshold;
            const speed = proximity * 15 + 3; // Speed increases as you get closer to edge (increased from 8 + 2)
            startAutoScroll('up', speed);
        } else if (distanceFromBottom < threshold) {
            // Near bottom edge - scroll down
            const proximity = (threshold - distanceFromBottom) / threshold;
            const speed = proximity * 15 + 3; // Increased from 8 + 2
            startAutoScroll('down', speed);
        } else {
            // Not near any edge - stop scrolling
            stopAutoScroll();
        }
    };

    // Drag and drop handlers
    const handleDragStart = (e, component, filteredIndex) => {
        e.stopPropagation();
        // Find the real index in the full components array
        const realIndex = components.findIndex(c => c.id === component.id);
        setDraggedComponent({ component, index: realIndex, filteredIndex });
        e.dataTransfer.effectAllowed = 'move';
        
        // Ensure clean state when starting drag
        stopAutoScroll();
        
        // Add global dragover listener for auto-scroll
        document.addEventListener('dragover', handleContainerDragOver);
    };

    const handleDragOver = (e, filteredIndex) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(filteredIndex);
        
        // Also check if we should stop auto-scroll when dragging over components
        const threshold = 100;
        const viewportHeight = window.innerHeight;
        const mouseY = e.clientY;
        const distanceFromTop = mouseY;
        const distanceFromBottom = viewportHeight - mouseY;
        
        // If we're not near edges while over a component, stop auto-scroll
        if (distanceFromTop >= threshold && distanceFromBottom >= threshold) {
            stopAutoScroll();
        }
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
        stopAutoScroll();
    };

    const handleDrop = (e, dropFilteredIndex) => {
        e.preventDefault();
        
        if (!draggedComponent || draggedComponent.filteredIndex === dropFilteredIndex) {
            setDraggedComponent(null);
            setDragOverIndex(null);
            stopAutoScroll();
            document.removeEventListener('dragover', handleContainerDragOver);
            return;
        }

        // Find the real component at the drop position in the filtered list
        const dropComponent = filteredComponents[dropFilteredIndex];
        const dropRealIndex = components.findIndex(c => c.id === dropComponent.id);
        
        const dragRealIndex = draggedComponent.index;
        const newComponents = [...components];
        
        // Remove the dragged component from its original position
        const [movedComponent] = newComponents.splice(dragRealIndex, 1);
        
        // Insert it at the new position
        newComponents.splice(dropRealIndex, 0, movedComponent);
        
        // Update positions based on new order
        const updatedComponents = newComponents.map((comp, index) => ({
            ...comp,
            position: index // + 1
        }));
        
        setComponents(updatedComponents);
        setDraggedComponent(null);
        setDragOverIndex(null);
        stopAutoScroll();
        document.removeEventListener('dragover', handleContainerDragOver);
        
        // Mark the moved component as having unsaved changes
        markComponentAsChanged(draggedComponent.component.id);
        
        // Show success message
        setShowAlert(true);
        setAlertMessage(`Component moved to position ${dropRealIndex + 1}`);
        setAlertType('success');
    };

    const handleDragEnd = () => {
        setDraggedComponent(null);
        setDragOverIndex(null);
        stopAutoScroll();
        document.removeEventListener('dragover', handleContainerDragOver);
    };
     
    // Set page title and load Tailwind CSS + Custom CSS
    useEffect(() => {
        document.title = "Ukie's Admin - Page Designer";
        
        // Add Tailwind CSS v3.4.3 CDN
        const tailwindLink = document.createElement('link');
        tailwindLink.id = 'tailwind-css-designer';
        tailwindLink.rel = 'stylesheet';
        tailwindLink.href = 'https://cdn.jsdelivr.net/npm/tailwindcss@3.4.3/tailwind.min.css';
        document.head.appendChild(tailwindLink);
        
        // Add custom CSS for designer page
        const customStyle = document.createElement('style');
        customStyle.id = 'designer-custom-css';
        customStyle.textContent = `
            /* Override Bootstrap primary color to match UkiesAdminPages */
            .btn-primary {
                background-color: #3b4f46 !important;
                border-color: #3b4f46 !important;
            }
            .btn-primary:hover {
                background-color: #567a70 !important;
                border-color: #567a70 !important;
            }
            .btn-primary:focus, .btn-primary.focus {
                background-color: #2d3d35 !important;
                border-color: #2d3d35 !important;
                box-shadow: 0 0 0 0.2rem rgba(59, 79, 70, 0.5) !important;
            }
            .btn-primary:not(:disabled):not(.disabled):active, .btn-primary:not(:disabled):not(.disabled).active {
                background-color: #243028 !important;
                border-color: #243028 !important;
            }
            .btn-outline-primary {
                color: #3b4f46 !important;
                border-color: #3b4f46 !important;
            }
            .btn-outline-primary:hover {
                background-color: #3b4f46 !important;
                border-color: #3b4f46 !important;
            }
            .btn-outline-primary:focus, .btn-outline-primary.focus {
                box-shadow: 0 0 0 0.2rem rgba(59, 79, 70, 0.5) !important;
            }
            .btn-outline-primary:not(:disabled):not(.disabled):active, .btn-outline-primary:not(:disabled):not(.disabled).active {
                background-color: #3b4f46 !important;
                border-color: #3b4f46 !important;
            }

            /* Keenicons */
            @font-face {
                font-display: block;
                font-family: keenicons-duotone;
                font-style: normal;
                font-weight: 400;
                src: url(/fonts/keenicons-duotone.DGcKa84C.ttf) format("truetype"), url(/fonts/keenicons-duotone.CKhy9TPl.woff) format("woff"), url(/fonts/keenicons-duotone.BzRXiZuU.svg) format("svg")
            }

            @font-face {
                font-display: block;
                font-family: keenicons-filled;
                font-style: normal;
                font-weight: 400;
                src: url(/fonts/keenicons-filled.CE921hZa.ttf) format("truetype"), url(/fonts/keenicons-filled.BkDGg1uE.woff) format("woff"), url(/fonts/keenicons-filled.CrMg3cU0.svg) format("svg")
            }

            /* Designer Page Custom Styles */
            .components-container img,
            .components-container video {
                height: auto;
                max-width: 100%;
            }
            .justify-center {
                justify-content: center;
            }
            .flex-col {
                flex-direction: column;
            }
            .items-end {
                align-items: flex-end;
            }
            
            /* Component wrapper styles */
            .component-wrapper {
                position: relative;
                transition: all 0.2s ease-in-out;
            }
            
            .component-wrapper.drag-over {
                transform: translateY(-10px);
                box-shadow: 0 10px 25px rgba(0,0,0,0.1);
            }
            
            .component-wrapper.being-dragged {
                opacity: 0.5;
                transform: scale(0.95);
            }
            
            /* Edit icon styles */
            .component-edit-icon {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 100px;
                height: 100px;
                background-color: var(--ukies-dark-green);
                border: 2px solid white;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                opacity: 0;
                transition: opacity 0.2s ease-in-out;
                z-index: 10;
                box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            }
            
            .component-edit-icon:hover {
                background-color: var(--ukies-dark-orange);
                filter: brightness(0.85);
                transform: translate(-50%, -50%) scale(1.05);
            }
            
            .component-wrapper:hover .component-edit-icon {
                opacity: 1;
            }
            
            .component-edit-icon svg {
                width: 53px;
                height: 53px;
                fill: white;
            }
            
            /* Drag button styles */
            .component-drag-icon {
                position: absolute;
                top: 50%;
                left: calc(50% + 90px);
                transform: translate(-50%, -50%);
                width: 40px;
                height: 40px;
                background-color: #6c757d;
                border: 2px solid white;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: grab;
                opacity: 0;
                transition: opacity 0.2s ease-in-out;
                z-index: 11;
                box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            }
            
            .component-drag-icon:hover {
                background-color: #5a6268;
                transform: translate(-50%, -50%) scale(1.1);
            }
            
            .component-drag-icon:active {
                cursor: grabbing;
            }
            
            .component-wrapper:hover .component-drag-icon {
                opacity: 1;
            }
            
            .component-drag-icon svg {
                width: 24px;
                height: 24px;
                fill: white;
            }
            
            /* Drop indicator line */
            .drop-indicator {
                position: absolute;
                left: 0;
                right: 0;
                height: 3px;
                margin: 0.5rem 0;
                background-color: var(--ukies-dark-orange);
                z-index: 100;
                border-radius: 1.5px;
                box-shadow: 0 0 8px rgba(0,0,0,0.2);
            }
            
            .drop-indicator.top {
                top: -1rem;
            }
            
            .drop-indicator.bottom {
                bottom: -1rem;
            }

            /* Custom switch colors for admin consistency */
            .form-check-input:checked {
                background-color: #28a745 !important;
                border-color: #28a745 !important;
            }
            
            .form-check-input:not(:checked) {
                background-color: #dc3545 !important;
                border-color: #dc3545 !important;
            }
            
            .form-check-input:focus {
                border-color: #80bdff !important;
                box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
            }
            
                         /* Component status switch with colored labels */
             .component-status-switch .form-check-input:checked {
                 background-color: #28a745 !important;
                 border-color: #28a745 !important;
             }
             
             .component-status-switch .form-check-input:not(:checked) {
                 background-color: #dc3545 !important;
                 border-color: #dc3545 !important;
             }
             
             .component-status-switch .form-check-label .active {
                 color: #28a745 !important;
                 font-weight: 600;
             }
             
             .component-status-switch .form-check-label .inactive {
                 color: #dc3545 !important;
                 font-weight: 600;
             }
            
             /* Archived switch with inverted colors */
             .archived-switch .form-check-input:checked {
                 background-color: #dc3545 !important; /* red when archived (true) */
                 border-color: #dc3545 !important;
             }
             .archived-switch .form-check-input:not(:checked) {
                 background-color: #28a745 !important; /* green when not archived (false) */
                 border-color: #28a745 !important;
             }
             .archived-switch .form-check-label .archived {
                 color: #dc3545 !important;
                 font-weight: 600;
             }
             .archived-switch .form-check-label .not-archived {
                 color: #28a745 !important;
                 font-weight: 600;
             }

             /* Filter toggles label baseline alignment */
             .filter-toggles .form-check-label {
                 margin-bottom: -1px !important;
             }

                         /* Full width HR that breaks out of container */
             .full-width-hr {
                 width: 100vw;
                 position: relative;
                 left: 50%;
                 right: 50%;
                 margin-left: -50vw;
                 margin-right: -50vw;
                 border: none;
                 border-top: 1px solid var(--ukies-dark-green);
                 margin-top: 1.5rem;
                 margin-bottom: 1.5rem;
                 opacity: 1;
             }
             
              /* Component settings form styling - matching UkiesAdminSeries.js */
              .offcanvas-body .form-control {
                  font-size: 0.9rem !important;
              }
              .offcanvas-body .form-select {
                  font-size: 0.9rem !important;
              }
              .offcanvas-body .form-label {
                  font-size: 0.7rem !important;
              }
              .offcanvas-body textarea.form-control {
                  font-size: 0.9rem !important;
              }
                             .offcanvas-body .form-check-label {
                   font-size: 0.9rem !important;
               }
               
               /* Array field tabbed interface styling */
               .array-field-tabs .nav-tabs {
                   border-bottom: none !important;
               }
               
               .array-field-tabs .nav-tabs .nav-link {
                   border-bottom: 1px solid #dee2e6 !important;
                   border-radius: 0.375rem 0.375rem 0 0 !important;
                   margin-bottom: -1px !important;
               }
               
               .array-field-tabs .nav-tabs .nav-link.active {
                   border-bottom-color: #fff !important;
                   background-color: #fff !important;
                   border-color: #dee2e6 #dee2e6 #fff !important;
               }
               
               .array-field-tabs .tab-content-wrapper {
                   border: 1px solid #dee2e6;
                   border-top: none;
                   border-radius: 0 0 0.375rem 0.375rem;
               }
               
               /* Array item action buttons */
               .array-item-actions .btn {
                   --bs-btn-padding-y: 0.125rem;
                   --bs-btn-padding-x: 0.25rem;
                   --bs-btn-font-size: 0.625rem;
               }

                /* Delete toggle switch */
                .delete-toggle-switch {
                    margin: 0rem;
                    padding: 0rem;
                    min-height: unset;
                }
                                
                .unsaved-changes-indicator {
                    box-shadow: 0 2px 8px rgba(255, 107, 53, 0.4) !important;
                }
                .component-wrapper:hover .inactive-component-overlay, .component-wrapper:hover .archived-component-overlay {
                    opacity: 0 !important;
                }

                .component-wrapper:has(> div > .structure) + .component-wrapper:has(> div > .structure) > div > .structure {
                    padding-top: 0;
                }

                /* EUI Button Styles */
                .eui-button-default.w-full {
                    width: 100%;
                }
                .eui-button-default.mt-4 {
                    margin-top: 1rem !important;
                }
                .eui-button-default, .eui-button-show-more {
                    align-items: center;
                    justify-content: center;
                    padding: .5rem 1rem;
                }
                .eui-button-default {
                    border-color: transparent;
                    border-radius: 0.375rem;
                    border-width: 1px;
                    cursor: pointer;
                    display: flex;
                    font-size: 0.875rem;
                    font-weight: 500;
                    height: 2.8rem;
                    min-width: 2.8rem;
                    text-decoration: none;
                    transition: transform .25s ease-in-out;
                }
                .eui-button-primary {
                    background: var(--ukies-button-color-default);
                    color: var(--ukies-button-font-color-default);
                }
        `;
        document.head.appendChild(customStyle);
        
        // Cleanup function to remove Tailwind and custom CSS when component unmounts
        return () => {
            const existingTailwindLink = document.getElementById('tailwind-css-designer');
            if (existingTailwindLink) {
                document.head.removeChild(existingTailwindLink);
            }
            
            const existingCustomStyle = document.getElementById('designer-custom-css');
            if (existingCustomStyle) {
                document.head.removeChild(existingCustomStyle);
            }
        };
    }, []);

    // Cleanup drag events on unmount
    useEffect(() => {
        return () => {
            stopAutoScroll();
            document.removeEventListener('dragover', handleContainerDragOver);
            if (autoScrollTimeout) {
                clearTimeout(autoScrollTimeout);
            }
        };
    }, [autoScrollTimeout]);

    // Fetch page details
    const fetchPage = async () => {
        if (!pageId) return;
        
        try {
            const response = await fetch('https://ukies-api-search.eliteswitch.com/indexes/webshops_pages/search', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    "q": pageId,
                    "sort": ["id:desc"],
                    "matchingStrategy": "all",
                    "facets": ["*"],
                    "filter": [],
                    "limit": 1
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.hits && data.hits.length > 0) {
                    setPage(data.hits[0]);
                } else {
                    setError('Pagina niet gevonden');
                }
            } else {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            console.error('Error fetching page:', error);
            setError(error.message);
        }
    };

    // Fetch page components
    const fetchComponents = async () => {
        if (!pageId) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch('https://ukies-api-search.eliteswitch.com/indexes/webshops_pages_components/search', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    "q": null,
                    "sort": ["position:asc"],
                    "matchingStrategy": "all",
                    "facets": ["*"],
                    "filter": [["page_id='" + pageId + "'"]],
                    "limit": 100
                })
            });

            if (response.ok) {
                const data = await response.json();
                // Get all components and sort by position
                const allComponents = (data.hits || [])
                    .sort((a, b) => (a.position || 0) - (b.position || 0));
                
                setComponents(allComponents);
                
                // Count active components for success message
                const activeCount = allComponents.filter(component => {
                    const isActive = component.component_status === null || component.component_status === undefined ? true : component.component_status === 1;
                    return isActive;
                }).length;
                
                setShowAlert(true);
                setAlertMessage(`${allComponents.length} componenten geladen (${activeCount} zichtbaar)!`);
                setAlertType('success');
            } else {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            console.error('Error fetching components:', error);
            setError(error.message);
            setShowAlert(true);
            setAlertMessage('Fout bij het ophalen van componenten. Probeer het opnieuw.');
            setAlertType('danger');
        } finally {
            setLoading(false);
        }
    };

    // Fetch forms from API
    const fetchForms = async () => {
        try {
            const response = await fetch('https://ukies-api-search.eliteswitch.com/indexes/webshops_forms/search', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    "q": "",
                    "sort": ["id:asc"],
                    "matchingStrategy": "all",
                    "facets": ["*"],
                    "limit": 1000
                })
            });

            if (response.ok) {
                const data = await response.json();
                // console.log('Fetched forms data:', data.hits || []);
                setForms(data.hits || []);
            } else {
                console.error('Failed to fetch forms:', response.statusText);
            }
        } catch (error) {
            console.error('Error fetching forms:', error);
        }
    };

    // Fetch widgets from API
    const fetchWidgets = async () => {
        try {
            const response = await fetch('https://ukies-api-search.eliteswitch.com/indexes/webshops_widgets/search', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    "q": "",
                    "sort": ["id:desc"],
                    "matchingStrategy": "all",
                    "facets": ["*"],
                    "filter": [["widget_status=1"],["widget_type!='usps'"]],
                    "limit": 1000
                })
            });

            if (response.ok) {
                const data = await response.json();
                // console.log('Fetched widgets data:', data.hits || []);
                const sortedWidgets = (data.hits || []).sort((a, b) => a.name.localeCompare(b.name));
                setWidgets(sortedWidgets);
            } else {
                console.error('Failed to fetch widgets:', response.statusText);
            }
        } catch (error) {
            console.error('Error fetching widgets:', error);
        }
    };

    // Handle component selection
    const handleComponentClick = (component) => {
        setSelectedComponent(component);
        setShowSettings(true);
        setDeleteEnabled(false); // Reset delete toggle for safety
        
        // Extract component type from HTML content
        const extractedType = extractComponentType(component.content);
        setComponentType(extractedType);
        
        // Initialize component settings
        const initialSettings = { ...component.settings || {} };
        setComponentSettings(initialSettings);
    };

    // Handle settings field changes
    const handleSettingsChange = (fieldKey, value) => {
        setComponentSettings(prev => ({
            ...prev,
            [fieldKey]: value
        }));
        
        // Mark component as having unsaved changes
        if (selectedComponent) {
            markComponentAsChanged(selectedComponent.id);
        }
        
        // If content is being updated, also update it in the component and selected component
        if (fieldKey === 'content') {
            // Update the component in the components array
            setComponents(prev => prev.map(comp => 
                comp.id === selectedComponent.id 
                    ? { ...comp, content: value }
                    : comp
            ));
            
            // Update the selected component
            setSelectedComponent(prev => ({
                ...prev,
                content: value
            }));
        }
    };

    // Handle component status change
    const handleComponentStatusChange = (isActive) => {
        const newStatus = isActive ? 1 : 0;
        
        // Update the component in the components array
        setComponents(prev => prev.map(comp => 
            comp.id === selectedComponent.id 
                ? { ...comp, component_status: newStatus }
                : comp
        ));
        
        // Update the selected component
        setSelectedComponent(prev => ({
            ...prev,
            component_status: newStatus
        }));
        
        // Mark component as having unsaved changes
        if (selectedComponent) {
            markComponentAsChanged(selectedComponent.id);
        }
        
        // Show status change message
        setShowAlert(true);
        setAlertMessage(`Component ${isActive ? 'geactiveerd' : 'gedeactiveerd'}`);
        setAlertType('success');
    };

    // Handle archived toggle
    const handleArchivedToggle = (shouldArchive) => {
        if (!selectedComponent) return;

        const updatedContent = updateArchivedStatusInHTML(selectedComponent.content, shouldArchive);

        // If archiving, also force visibility off (component_status = 0)
        const updatedStatus = shouldArchive ? 0 : (selectedComponent.component_status ?? 0);

        // Update components array
        setComponents(prev => prev.map(comp =>
            comp.id === selectedComponent.id
                ? { ...comp, content: updatedContent, component_status: updatedStatus }
                : comp
        ));

        // Update selected component
        setSelectedComponent(prev => ({
            ...prev,
            content: updatedContent,
            component_status: updatedStatus
        }));

        // Keep componentSettings.content in sync when present
        setComponentSettings(prev => ({
            ...(prev || {}),
            content: updatedContent
        }));

        // Mark as changed
        markComponentAsChanged(selectedComponent.id);

        // Show feedback
        setShowAlert(true);
        setAlertMessage(shouldArchive ? 'Component gearchiveerd' : 'Component uit archief gehaald');
        setAlertType('success');
    };

    // Handle component label change
    const handleComponentLabelChange = (e) => {
        const newLabel = e.target.value;
        
        // Update the component in the components array
        setComponents(prev => prev.map(comp => 
            comp.id === selectedComponent.id 
                ? { ...comp, label: newLabel }
                : comp
        ));
        
        // Update the selected component
        setSelectedComponent(prev => ({
            ...prev,
            label: newLabel
        }));
        
        // Mark component as having unsaved changes
        if (selectedComponent) {
            markComponentAsChanged(selectedComponent.id);
        }
    };

    // Handle settings save
    const handleSettingsSave = async () => {
        if (!selectedComponent) return;
        
        setLoading(true);
        
        try {
            // Prepare the payload for the API
            const payload = {
                label: selectedComponent.label || '',
                component_status: selectedComponent.component_status === 0 ? 0 : 1,
                position: selectedComponent.position || 0,
                content: componentSettings.content || selectedComponent.content || ''
            };
            
            // Make API call to save the component
            const response = await fetch(`https://ukies-api.eliteswitch.com/api/webshops/pages/component/${selectedComponent.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${apiToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json();
                // console.log('Component saved successfully:', data);
                
                // Update the component in the local state with new settings and content
                const updatedComponent = {
                    ...selectedComponent,
                    settings: componentSettings,
                    // Include content if it was updated through componentSettings
                    ...(componentSettings.content && { content: componentSettings.content }),
                    // Update with any data from the API response if needed
                    ...(data.data?.payload && {
                        label: data.data.payload.label,
                        component_status: data.data.payload.component_status,
                        position: data.data.payload.position,
                        content: data.data.payload.content,
                        updated_at: data.data.payload.updated_at
                    })
                };
                
                setComponents(prev => prev.map(comp => 
                    comp.id === selectedComponent.id 
                        ? updatedComponent
                        : comp
                ));
                
                setSelectedComponent(updatedComponent);
                
                // Mark component as saved (remove from unsaved changes)
                markComponentAsSaved(selectedComponent.id);
                
                // Show success message
                setShowAlert(true);
                setAlertMessage(`Instellingen opgeslagen! (Task ID: ${data.task_id})`);
                setAlertType('success');
                
            } else {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
        } catch (error) {
            console.error('Error saving settings:', error);
            setShowAlert(true);
            setAlertMessage('Fout bij het opslaan van instellingen. Probeer het opnieuw.');
            setAlertType('danger');
        } finally {
            setLoading(false);
        }
    };

    // Handle settings panel close
    const handleCloseSettings = () => {
        setShowSettings(false);
        setSelectedComponent(null);
        setComponentType(null);
        setComponentSettings({});
        setDeleteEnabled(false); // Reset delete toggle for safety
    };

    // Open the "add component" dialog with a clean form
    const handleOpenAddModal = () => {
        setNewComponentLabel('');
        setNewComponentType('');
        setNewComponentVisible(true);
        setShowAddModal(true);
    };

    // Close the "add component" dialog
    const handleCloseAddModal = () => {
        setShowAddModal(false);
    };

    // Create a new component at the top of the list
    const handleCreateComponent = async () => {
        if (!newComponentType) return;

        setLoading(true);

        try {
            // The sample payload didn't include content, but the dialog collects a
            // type, so we seed a minimal wrapper div carrying the ukies-* class.
            // extractComponentType() reads this class, and opening the component in
            // the settings panel will load the matching editor (which then rebuilds
            // the full HTML from its defaults on first edit).
            const payload = {
                label: newComponentLabel || '',
                position: 0,
                // NOTE: you said component_status should always be 1, but the dialog
                // has a visibility toggle — wiring it here so the toggle is functional.
                // Change to a hardcoded 1 if creates must always be visible.
                component_status: newComponentVisible ? 1 : 0,
                page_id: pageId,
                content: `<div class="${newComponentType}"></div>`
            };

            const response = await fetch('https://ukies-api.eliteswitch.com/api/webshops/pages/component', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json();
                const created = data.data?.payload || {};

                // Build a local component object from the response (falling back to
                // the values we sent). Prepend it so it appears at the top of the list.
                const newComponent = {
                    id: created.id,
                    page_id: pageId,
                    label: created.label ?? payload.label,
                    position: created.position ?? 0,
                    component_status: created.component_status ?? payload.component_status,
                    content: created.content ?? payload.content,
                    settings: {},
                    created_at: created.created_at,
                    ...created
                };

                setComponents(prev => [newComponent, ...prev]);

                setShowAlert(true);
                setAlertMessage(`Component aangemaakt!${data.task_id ? ` (Task ID: ${data.task_id})` : ''}`);
                setAlertType('success');

                handleCloseAddModal();
            } else {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            console.error('Error creating component:', error);
            setShowAlert(true);
            setAlertMessage('Fout bij het aanmaken van component. Probeer het opnieuw.');
            setAlertType('danger');
        } finally {
            setLoading(false);
        }
    };

    // Load data on component mount
    useEffect(() => {
        if (token && pageId) {
            fetchPage();
            fetchComponents();
            fetchForms();
            fetchWidgets();
        }
    }, [token, pageId]);

    // Auto-hide alert after 5 seconds
    useEffect(() => {
        if (showAlert) {
            const timer = setTimeout(() => {
                setShowAlert(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [showAlert]);

    // Warn user before leaving page with unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (unsavedChanges.size > 0) {
                e.preventDefault();
                e.returnValue = 'Er zijn niet-opgeslagen wijzigingen. Weet je zeker dat je de pagina wilt verlaten?';
                return 'Er zijn niet-opgeslagen wijzigingen. Weet je zeker dat je de pagina wilt verlaten?';
            }
        };

        if (unsavedChanges.size > 0) {
            window.addEventListener('beforeunload', handleBeforeUnload);
        } else {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        }

        // Cleanup on unmount
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [unsavedChanges]);

    // Auth state listener
    onAuthStateChanged(auth, (user) => {
        if (user) {
            // User is signed in > Refresh token
        } else {
            // User is signed out > Remove local storage
            localStorage.removeItem('ukies-webinars-admin')
            localStorage.removeItem('ukies-webinars-accessToken')
            setToken()
            setUser()
        }
    })

    if(!token) {
        return <Login user={user} setUser={setUser} token={token} setToken={setToken} />
    }

    if (error) {
        return (
            <>
                <Navigation user={user} currentPage='pages'/>
                <Container style={{marginTop:'70px', padding:'2rem 0'}}>
                    <div className="text-center py-5">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="mb-3 text-danger" style={{opacity: 0.5}}>
                            <path d="M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17Z" fill="currentColor"/>
                        </svg>
                        <h4 className="text-danger">Fout</h4>
                        <p className="text-muted">{error}</p>
                        <Button variant="primary" onClick={() => navigate('/ukies-admin/pages')}>
                            Terug naar Paginas
                        </Button>
                    </div>
                </Container>
            </>
        );
    }

    return (
        <>
            <Navigation user={user} currentPage='pages'/>
            
            {showAlert && (
                <Alert 
                    variant={alertType} 
                    dismissible 
                    onClose={() => setShowAlert(false)} 
                    style={{
                        position: 'fixed',
                        top: '15px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1060,
                        minWidth: '300px',
                        maxWidth: '600px'
                    }}
                >
                    {alertMessage}
                </Alert>
            )}
            
            <Container fluid style={{marginTop:'50px', padding:'1rem 0'}}>
                 <Row>
                     <Col md={12}>
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <h1 style={{fontSize:'2.5rem'}}>Page Designer</h1>
                                {page && (
                                    <div>
                                        <h5 className="text-muted">{page.title || page.name || `Page ${page.id}`}</h5>
                                    </div>
                                )}
                            </div>
                            <div className="d-flex gap-2">
                                <Button variant="success" onClick={handleOpenAddModal}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-plus-lg me-2" viewBox="0 0 16 16" style={{marginTop:'-1px'}}>
                                        <path fillRule="evenodd" d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2"/>
                                    </svg>
                                    Component toevoegen
                                </Button>
                                <Button variant="primary" onClick={() => navigate('/ukies-admin/pages')}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-left me-2" viewBox="0 0 16 16" style={{marginTop:'-1px'}}>
                                        <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
                                    </svg>
                                    Terug
                                </Button>
                            </div>
                        </div>
                        
                        <div className="mb-4 d-flex align-items-center gap-3 flex-wrap filter-toggles">
                            <span className="text-muted">Toon:</span>
                            <Form.Check 
                                type="switch"
                                id="show-visible-toggle"
                                label={<span className={showVisibleComponents ? 'active' : 'inactive'}>Zichtbare componenten</span>}
                                checked={showVisibleComponents}
                                onChange={(e) => setShowVisibleComponents(e.target.checked)}
                                style={{display:'flex', alignItems:'center', gap: '0.2rem'}}
                            />
                            <Form.Check 
                                type="switch"
                                id="show-invisible-toggle"
                                label={<span className={showInvisibleComponents ? 'active' : 'inactive'}>Onzichtbare componenten</span>}
                                checked={showInvisibleComponents}
                                onChange={(e) => setShowInvisibleComponents(e.target.checked)}
                                style={{display:'flex', alignItems:'center', gap: '0.2rem', marginLeft:'10px'}}
                            />
                            <Form.Check 
                                type="switch"
                                id="show-archived-toggle"
                                label={<span className={showArchivedComponents ? 'active' : 'inactive'}>Gearchiveerde componenten</span>}
                                checked={showArchivedComponents}
                                onChange={(e) => setShowArchivedComponents(e.target.checked)}
                                style={{display:'flex', alignItems:'center', gap: '0.2rem', marginLeft:'10px'}}
                            />
                        </div>

                        <hr className="full-width-hr" />
                        
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-3 text-muted">Componenten laden...</p>
                            </div>
                        ) : filteredComponents.length > 0 ? (
                            <div className="components-container" onDragOver={handleContainerDragOver}>
                                {filteredComponents.map((component, index) => (
                                    <div 
                                        key={component.id} 
                                        className={`component-wrapper ${
                                            draggedComponent?.component.id === component.id ? 'being-dragged' : ''
                                        } ${
                                            dragOverIndex === index ? 'drag-over' : ''
                                        } ${
                                            component.component_status === 0 ? 'inactive' : ''
                                        }`}
                                        style={{
                                            transition: 'all 0.2s ease-in-out',
                                            marginBottom: '0'
                                        }}
                                        draggable="true"
                                        onDragStart={(e) => handleDragStart(e, component, index)}
                                        onDragOver={(e) => handleDragOver(e, index)}
                                        onDragLeave={handleDragLeave}
                                        onDrop={(e) => handleDrop(e, index)}
                                        onDragEnd={handleDragEnd}
                                    >
                                        {/* Drop indicator line */}
                                        {dragOverIndex === index && draggedComponent && (
                                            <div className={`drop-indicator ${draggedComponent.filteredIndex > index ? 'top' : 'bottom'}`} />
                                        )}
                                        
                                        {/* Edit Icon - Centered */}
                                        <div 
                                            className="component-edit-icon"
                                            onClick={() => handleComponentClick(component)}
                                            title="Component bewerken"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                                            </svg>
                                            
                                            {/* Unsaved changes indicator */}
                                            {hasUnsavedChanges(component.id) && (
                                                <div 
                                                    className="unsaved-changes-indicator"
                                                    title="Niet opgeslagen wijzigingen"
                                                    style={{
                                                        position: 'absolute',
                                                        top: '-5px',
                                                        right: '-5px',
                                                        width: '20px',
                                                        height: '20px',
                                                        backgroundColor: '#ff6b35',
                                                        border: '2px solid white',
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '10px',
                                                        fontWeight: 'bold',
                                                        color: 'white',
                                                        zIndex: 12,
                                                        animation: 'pulse 2s infinite'
                                                    }}
                                                >
                                                    !
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Drag Icon - Next to edit button */}
                                        <div 
                                            className="component-drag-icon"
                                            title="Sleep component om positie te wijzigen"
                                            onMouseDown={(e) => e.stopPropagation()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                                {/* Up arrow */}
                                                <path d="M7 10l5-5 5 5z"/>
                                                {/* Down arrow */}
                                                <path d="M7 14l5 5 5-5z"/>
                                            </svg>
                                        </div>
                                        
                                                                {component.content ? (
                            <HTMLWithScripts 
                                html={component.content}
                                style={{ 
                                    minHeight: '100px'
                                }}
                                componentId={component.id}
                                forms={forms}
                                widgets={widgets}
                            />
                        ) : (
                                            <div 
                                                className="text-center text-muted py-4"
                                                style={{ 
                                                    minHeight: '60px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    opacity: 0.5
                                                }}
                                            >
                                                <em>Geen content beschikbaar</em>
                                            </div>
                                        )}
                                        
                                        {/* Inactive component overlay */}
                                        {component.component_status === 0 && (
                                            <>
                                                { isComponentArchived(component.content)
                                                ? (
                                                    <div 
                                                        className="archived-component-overlay"
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            width: '100vw',
                                                            left: '50%',
                                                            right: '50%',
                                                            marginLeft: '-50vw',
                                                            marginRight: '-50vw',
                                                            bottom: 0,
                                                            backgroundColor: '#91071450',
                                                            zIndex: 6,
                                                            pointerEvents: 'none',
                                                            display: 'flex',
                                                            alignItems: 'end',
                                                            justifyContent: 'center',
                                                            padding: '1rem',
                                                            transition: 'all 0.5s ease-in-out',
                                                            opacity: 1,
                                                        }}
                                                    >
                                                    <div 
                                                            style={{
                                                                backgroundColor: '#91071490 ',
                                                                color: 'white',
                                                                padding: '8px 16px',
                                                                borderRadius: '20px',
                                                                fontSize: '0.85rem',
                                                                fontWeight: '600',
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                pointerEvents: 'auto',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M12 4.5C7.5 4.5 3.75 7.5 1.5 12C3.75 16.5 7.5 19.5 12 19.5C16.5 19.5 20.25 16.5 22.5 12C20.25 7.5 16.5 4.5 12 4.5ZM12 17C9.75 17 7.5 15.75 7.5 12C7.5 8.25 9.75 7 12 7C14.25 7 16.5 8.25 16.5 12C16.5 15.75 14.25 17 12 17ZM12 9C10.5 9 9 10.5 9 12C9 13.5 10.5 15 12 15C13.5 15 15 13.5 15 12C15 10.5 13.5 9 12 9Z" fill="currentColor"/>
                                                        </svg>
                                                            <span style={{lineHeight: '11px'}}>Component gearchiveerd</span>
                                                        </div>
                                                    </div>
                                                    )
                                                : (
                                                    <div 
                                                        className="inactive-component-overlay"
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            width: '100vw',
                                                            left: '50%',
                                                            right: '50%',
                                                            marginLeft: '-50vw',
                                                            marginRight: '-50vw',
                                                            bottom: 0,
                                                            backgroundColor: 'rgba(128, 128, 128, 0.7)',
                                                            zIndex: 6,
                                                            pointerEvents: 'none',
                                                            display: 'flex',
                                                            alignItems: 'end',
                                                            justifyContent: 'center',
                                                            padding: '1rem',
                                                            transition: 'all 0.5s ease-in-out',
                                                            opacity: 1,
                                                        }}
                                                    >
                                                    <div 
                                                            style={{
                                                                backgroundColor: 'rgba(58, 58, 58, 0.8)',
                                                                color: 'white',
                                                                padding: '8px 16px',
                                                                borderRadius: '20px',
                                                                fontSize: '0.85rem',
                                                                fontWeight: '600',
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                pointerEvents: 'auto',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M12 4.5C7.5 4.5 3.75 7.5 1.5 12C3.75 16.5 7.5 19.5 12 19.5C16.5 19.5 20.25 16.5 22.5 12C20.25 7.5 16.5 4.5 12 4.5ZM12 17C9.75 17 7.5 15.75 7.5 12C7.5 8.25 9.75 7 12 7C14.25 7 16.5 8.25 16.5 12C16.5 15.75 14.25 17 12 17ZM12 9C10.5 9 9 10.5 9 12C9 13.5 10.5 15 12 15C13.5 15 15 13.5 15 12C15 10.5 13.5 9 12 9Z" fill="currentColor"/>
                                                        </svg>
                                                            <span style={{lineHeight: '11px'}}>Component niet zichtbaar</span>
                                                        </div>
                                                    </div>
                                                    )}
                                            </>
                                        )}
                                        
                                         {/* Unsaved changes overlay */}
                                         {hasUnsavedChanges(component.id) && (
                                             <div 
                                                 className="unsaved-changes-overlay"
                                                 style={{
                                                     position: 'absolute',
                                                     top: 0,
                                                     width: '100vw',
                                                     left: '50%',
                                                     right: '50%',
                                                     marginLeft: '-50vw',
                                                     marginRight: '-50vw',
                                                     bottom: 0,
                                                     backgroundColor: 'rgba(255, 107, 53, 0)',
                                                     border: '4px solid #ff6b35',
                                                     borderRadius: '4px',
                                                     zIndex: 8,
                                                     pointerEvents: 'none',
                                                     display: 'flex',
                                                     alignItems: 'start',
                                                     justifyContent: 'center',
                                                     padding: '1rem'
                                                 }}
                                             >
                                                <div 
                                                     style={{
                                                         backgroundColor: '#ff6b35',
                                                         color: 'white',
                                                         padding: '8px 16px',
                                                         borderRadius: '20px',
                                                         fontSize: '0.85rem',
                                                         fontWeight: '600',
                                                         boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                         display: 'flex',
                                                         alignItems: 'center',
                                                         gap: '4px',
                                                         pointerEvents: 'auto',
                                                         cursor: 'pointer'
                                                     }}
                                                     onClick={async () => {
                                                         // Find the component in the current state and trigger save
                                                         const componentToSave = components.find(comp => comp.id === component.id);
                                                         if (componentToSave) {
                                                             // Set the component as selected temporarily to use handleSettingsSave
                                                             const originalSelected = selectedComponent;
                                                             setSelectedComponent(componentToSave);
                                                             
                                                             // Get the current settings for this component
                                                             const currentSettings = componentSettings && selectedComponent?.id === componentToSave.id 
                                                                 ? componentSettings 
                                                                 : componentToSave.settings || {};
                                                             
                                                             try {
                                                                 setLoading(true);
                                                                 
                                                                 const payload = {
                                                                     label: componentToSave.label || '',
                                                                     component_status: componentToSave.component_status === 0 ? 0 : 1,
                                                                     position: componentToSave.position || 0,
                                                                     content: currentSettings.content || componentToSave.content || ''
                                                                 };
                                                                 
                                                                 const response = await fetch(`https://ukies-api.eliteswitch.com/api/webshops/pages/component/${componentToSave.id}`, {
                                                                     method: 'PUT',
                                                                     headers: {
                                                                         'Authorization': `Bearer ${apiToken}`,
                                                                         'Content-Type': 'application/json'
                                                                     },
                                                                     body: JSON.stringify(payload)
                                                                 });
                                                                 
                                                                 if (response.ok) {
                                                                     const data = await response.json();
                                                                     
                                                                     // Update the component in the local state
                                                                     const updatedComponent = {
                                                                         ...componentToSave,
                                                                         settings: currentSettings,
                                                                         ...(currentSettings.content && { content: currentSettings.content }),
                                                                         ...(data.data?.payload && {
                                                                             label: data.data.payload.label,
                                                                             component_status: data.data.payload.component_status,
                                                                             position: data.data.payload.position,
                                                                             content: data.data.payload.content,
                                                                             updated_at: data.data.payload.updated_at
                                                                         })
                                                                     };
                                                                     
                                                                     setComponents(prev => prev.map(comp => 
                                                                         comp.id === componentToSave.id 
                                                                             ? updatedComponent
                                                                             : comp
                                                                     ));
                                                                     
                                                                     // Update selected component if it's the same one
                                                                     if (originalSelected?.id === componentToSave.id) {
                                                                         setSelectedComponent(updatedComponent);
                                                                     } else {
                                                                         setSelectedComponent(originalSelected);
                                                                     }
                                                                     
                                                                     // Mark component as saved
                                                                     markComponentAsSaved(componentToSave.id);
                                                                     
                                                                     // Show success message
                                                                     setShowAlert(true);
                                                                     setAlertMessage(`Instellingen opgeslagen! (Task ID: ${data.task_id})`);
                                                                     setAlertType('success');
                                                                 } else {
                                                                     throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                                                                 }
                                                             } catch (error) {
                                                                 console.error('Error saving settings:', error);
                                                                 setShowAlert(true);
                                                                 setAlertMessage('Fout bij het opslaan van instellingen. Probeer het opnieuw.');
                                                                 setAlertType('danger');
                                                                 // Restore original selected component
                                                                 setSelectedComponent(originalSelected);
                                                             } finally {
                                                                 setLoading(false);
                                                             }
                                                         }
                                                     }}
                                                     title="Klik om op te slaan"
                                                 >
                                                     <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                         <path d="M17,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V7L17,3M19,19H5V5H16.17L19,7.83V19M12,12A3,3 0 0,0 9,15A3,3 0 0,0 12,18A3,3 0 0,0 15,15A3,3 0 0,0 12,12M6,6V10H15V6H6Z"/>
                                                     </svg>
                                                     <span style={{lineHeight: '11px'}}>Nog niet opgeslagen</span>
                                                 </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="mb-3" style={{opacity: 0.3}}>
                                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" fill="currentColor"/>
                                </svg>
                                <h6 className="text-muted">
                                    Geen Componenten
                                </h6>
                                <p className="text-muted small mb-0">
                                    Deze pagina heeft geen componenten om weer te geven.
                                </p>
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>

            {/* Settings Panel */}
            <Offcanvas show={showSettings} onHide={handleCloseSettings} placement="end" style={{width: '100%', maxWidth:'800px'}}>
                <Offcanvas.Header closeButton>
                    <div>
                        <Offcanvas.Title className="d-flex align-items-center gap-2">
                            {selectedComponent?.label || 'Component Instellingen'}
                            {selectedComponent && hasUnsavedChanges(selectedComponent.id) && (
                                <span 
                                    className="badge bg-danger ms-2"
                                    style={{ fontSize: '0.7rem' }}
                                    title="Niet opgeslagen wijzigingen"
                                >
                                    Nog niet opgeslagen
                                </span>
                            )}
                        </Offcanvas.Title>
                        {/* Component ID and Position - Under Header */}
                        {selectedComponent && (
                            <div>
                                <small className="text-muted">
                                    ID: {selectedComponent.id} • Position: {(selectedComponent.position || 0) + 1} • Created: {new Date(selectedComponent.created_at).toLocaleString('nl-NL')}
                                </small>
                            </div>
                        )}
                    </div>    
                 </Offcanvas.Header>
                 

                 
                 <Offcanvas.Body className="pt-0">
                     {selectedComponent && (
                         <div>
                             {/* Archived Switch (always visible) */}
                             <div className="py-3 archived-switch">
                                 <Form.Check
                                     type="switch"
                                     id="component-archived-toggle"
                                     label={
                                         <span className={isComponentArchived(selectedComponent.content) ? 'archived' : 'not-archived'}>
                                             {isComponentArchived(selectedComponent.content) ? 'Gearchiveerd' : 'Niet gearchiveerd'}
                                         </span>
                                     }
                                     checked={isComponentArchived(selectedComponent.content)}
                                     onChange={(e) => handleArchivedToggle(e.target.checked)}
                                 />
                             </div>

                             {/* Component Status Switch (hidden when archived) */}
                             {!isComponentArchived(selectedComponent.content) && (
                                 <div className="component-status-switch pb-4">
                                     <Form.Check 
                                         type="switch"
                                         id="component-status-toggle"
                                         label={
                                             <span className={selectedComponent.component_status === 0 ? 'inactive' : 'active'}>
                                                 {selectedComponent.component_status === 0 ? 'Niet zichtbaar' : 'Zichtbaar'}
                                             </span>
                                         }
                                         checked={selectedComponent.component_status !== 0}
                                         onChange={(e) => handleComponentStatusChange(e.target.checked)}
                                     />
                                 </div>
                             )}

                            {/* Component Label */}
                            <Form.Group className="mb-3">
                                <Form.Label>Label (in Eliteswitch)</Form.Label>
                                <Form.Control
                                    type="text"
                                     value={selectedComponent.label || ''}
                                     disabled={isComponentArchived(selectedComponent.content)}
                                    onChange={handleComponentLabelChange}
                                    placeholder="Voer component label in"
                                />
                            </Form.Group>

                            {/* Content Type */}
                            <Form.Group className="mb-3">
                                <Form.Label>Content Type</Form.Label>
                                <Form.Select
                                     disabled={!!componentType || isComponentArchived(selectedComponent.content)}
                                    value={componentType || ''}
                                    onChange={(e) => {
                                        const newComponentType = e.target.value;
                                        setComponentType(newComponentType);
                                        
                                        if (selectedComponent) {
                                            // Update the HTML content with the new component type class
                                            const updatedContent = updateComponentTypeInHTML(selectedComponent.content, newComponentType);
                                            
                                            // Update the component in the components array
                                            setComponents(prev => prev.map(comp => 
                                                comp.id === selectedComponent.id 
                                                    ? { ...comp, content: updatedContent }
                                                    : comp
                                            ));
                                            
                                            // Update the selected component
                                            setSelectedComponent(prev => ({
                                                ...prev,
                                                content: updatedContent
                                            }));
                                            
                                            // Update component settings to reflect the new content
                                            setComponentSettings(prev => ({
                                                ...prev,
                                                content: updatedContent
                                            }));
                                            
                                            // Mark component as having unsaved changes
                                            markComponentAsChanged(selectedComponent.id);
                                        }
                                    }}
                                >
                                    <option value="">Selecteer een type...</option>
                                    <option value="ukies-fullsize-slider-banner">Fullsize slider</option>
                                    <option value="ukies-fullsize-banner">Fullsize banner</option>
                                    <option value="ukies-2-2-block">50/50 blok</option>
                                    <option value="ukies-faq">Veelgestelde vragen</option>
                                    <option value="ukies-text-with-blocks">Tekst met blokken</option>
                                    <option value="ukies-highlight-bar">Highlight bar</option>
                                    <option value="ukies-strapline-title">Strapline en titel</option>
                                    <option value="ukies-photo-text">Foto met tekst</option>
                                    <option value="ukies-form">Eliteswitch formulier</option>
                                    <option value="ukies-widget">Eliteswitch widget</option>
                                    <option value="ukies-photo-gallery">Fotogalerij</option>
                                    <option value="ukies-spacer">Spacer</option>
                                    <option value="ukies-no-edits">Niet aanpasbaar</option>
                                </Form.Select>
                            </Form.Group>

                            {isComponentArchived(selectedComponent.content) ? (
                                <Alert variant="info" className="small" style={{ backgroundColor: '#91071420', border: 'none', color: 'var(--ukies-disabled)'}}>
                                    <Alert.Heading className="h6">Component gearchiveerd</Alert.Heading>
                                    <p className="mb-0">
                                        Dit component is gearchiveerd en kan niet worden bewerkt. Haal het uit het archief om bewerkingen toe te staan.
                                    </p>
                                </Alert>
                            ) : (
                                <>
                                    <h6 className="mb-2 mt-4" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '8px' }}>
                                        Content
                                    </h6>
                                    {/* Component type specific settings  */}
                                    <ComponentSettingsForm 
                                        componentType={componentType}
                                        componentSettings={componentSettings}
                                        setComponentSettings={setComponentSettings}
                                        onChange={handleSettingsChange}
                                        selectedComponent={selectedComponent}
                                        setSelectedComponent={setSelectedComponent}
                                        forms={forms}
                                        widgets={widgets}
                                    />
                                </>
                            )}

                                                           {/* Footer with action buttons */}
                              <div className="d-flex justify-content-between align-items-center mt-4 pt-3" style={{ borderTop: '1px solid #dee2e6' }}>
                                  {/* Left side: Delete toggle only */}
                                  <div className="d-flex align-items-center gap-2">

                                  </div>
                                  
                                  {/* Right side: Delete, Cancel and Save buttons */}
                                  <div className="d-flex gap-2 align-items-center">
                                        {componentType !== 'ukies-no-edits' && (
                                            <>
                                                <Form.Check
                                                    type="switch"
                                                    id="delete-enabled-switch"
                                                    checked={deleteEnabled}
                                                    onChange={(e) => setDeleteEnabled(e.target.checked)}
                                                    className="delete-toggle-switch"
                                                    label=""
                                                />
                                                <Button 
                                                    variant="danger"
                                                    disabled={!deleteEnabled}
                                                    onClick={() => {
                                                        if (window.confirm('Weet je zeker dat je dit component wilt verwijderen?')) {
                                                            // TODO: Implement component deletion
                                                            alert('Component verwijderen is nog niet mogelijk');
                                                        }
                                                    }}
                                                    style={{marginRight:'3rem'}}
                                                >
                                                    Verwijderen
                                                </Button>
                                            </>
                                        )}
                                        <Button 
                                            variant="secondary" 
                                            onClick={handleCloseSettings}
                                        >
                                            Annuleren
                                        </Button>
                                        <Button 
                                            variant="success" 
                                            onClick={handleSettingsSave}
                                        >
                                            Opslaan
                                        </Button>
                                  </div>
                              </div>

                             {/* Debug Info (collapsible) */}
                            <details className="mt-4">
                                <summary className="cursor-pointer text-muted small">
                                    <strong>Debug Information</strong>
                                </summary>
                                <div className="mt-2">
                                    <small className="d-block text-muted mb-2">Component Data:</small>
                                    <pre style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '5px',
                                        padding: '10px',
                                        fontSize: '11px',
                                        maxHeight: '200px',
                                        overflow: 'auto'
                                    }}>
                                        {JSON.stringify(selectedComponent, null, 2)}
                                    </pre>
                                    
                                    <small className="d-block text-muted mb-2 mt-3">Current Settings:</small>
                                    <pre style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '5px',
                                        padding: '10px',
                                        fontSize: '11px',
                                        maxHeight: '150px',
                                        overflow: 'auto'
                                    }}>
                                        {JSON.stringify(componentSettings, null, 2)}
                                    </pre>
                                </div>
                            </details>
                        </div>
                    )}
                </Offcanvas.Body>
            </Offcanvas>

            {/* Add Component Dialog */}
            <Modal show={showAddModal} onHide={handleCloseAddModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Component toevoegen</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Label (in Eliteswitch)</Form.Label>
                        <Form.Control
                            type="text"
                            value={newComponentLabel}
                            onChange={(e) => setNewComponentLabel(e.target.value)}
                            placeholder="Voer component label in"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Content Type</Form.Label>
                        <Form.Select
                            value={newComponentType}
                            onChange={(e) => setNewComponentType(e.target.value)}
                        >
                            <option value="">Selecteer een type...</option>
                            <option value="ukies-fullsize-slider-banner">Fullsize slider</option>
                            <option value="ukies-fullsize-banner">Fullsize banner</option>
                            <option value="ukies-2-2-block">50/50 blok</option>
                            <option value="ukies-faq">Veelgestelde vragen</option>
                            <option value="ukies-text-with-blocks">Tekst met blokken</option>
                            <option value="ukies-highlight-bar">Highlight bar</option>
                            <option value="ukies-strapline-title">Strapline en titel</option>
                            <option value="ukies-photo-text">Foto met tekst</option>
                            <option value="ukies-form">Eliteswitch formulier</option>
                            <option value="ukies-widget">Eliteswitch widget</option>
                            <option value="ukies-photo-gallery">Fotogalerij</option>
                            <option value="ukies-spacer">Spacer</option>
                            <option value="ukies-no-edits">Niet aanpasbaar</option>
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="component-status-switch">
                        <Form.Check
                            type="switch"
                            id="new-component-visible-toggle"
                            label={
                                <span className={newComponentVisible ? 'active' : 'inactive'}>
                                    Direct zichtbaar op website
                                </span>
                            }
                            checked={newComponentVisible}
                            onChange={(e) => setNewComponentVisible(e.target.checked)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseAddModal}>
                        Annuleren
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleCreateComponent}
                        disabled={!newComponentType}
                    >
                        Aanmaken
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}